import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { FiGlobe } from "react-icons/fi";

const MENU_ID = "language-selector-menu";
const MENU_WIDTH_PX = 144;

const LANGUAGES = [
  { code: "fr", labelKey: "language.fr" },
  { code: "en", labelKey: "language.en" },
];

const LanguageSelector = ({ variant = "dropdown", className = "" }) => {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const ref = useRef(null);

  const updateMenuPos = useCallback(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    setMenuPos({
      top: rect.bottom + 4,
      left: Math.max(8, rect.right - MENU_WIDTH_PX),
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    updateMenuPos();
    const closeOnOutside = (e) => {
      const menu = document.getElementById(MENU_ID);
      if (ref.current?.contains(e.target) || menu?.contains(e.target)) return;
      setOpen(false);
    };
    const reposition = () => updateMenuPos();

    document.addEventListener("click", closeOnOutside);
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);

    return () => {
      document.removeEventListener("click", closeOnOutside);
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [open, updateMenuPos]);

  if (variant === "buttons") {
    return (
      <div className={`flex gap-2 ${className}`}>
        {LANGUAGES.map(({ code, labelKey }) => (
          <button
            key={code}
            type="button"
            onClick={() => i18n.changeLanguage(code)}
            className={`min-h-touch px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
              i18n.language?.startsWith(code)
                ? "bg-brand text-white hover:bg-brand-dark hover:scale-[1.02] active:scale-[0.98]"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:scale-[1.02] active:scale-[0.98]"
            }`}
          >
            {t(labelKey)}
          </button>
        ))}
      </div>
    );
  }

  const menu =
    open &&
    menuPos &&
    createPortal(
      <div
        id={MENU_ID}
        className="fixed py-1 w-36 bg-white rounded-xl shadow-lg border border-gray-100 z-[10000]"
        style={{ top: menuPos.top, left: menuPos.left }}
      >
        {LANGUAGES.map(({ code, labelKey }) => (
          <button
            key={code}
            type="button"
            onClick={() => {
              i18n.changeLanguage(code);
              setOpen(false);
            }}
            className={`block w-full text-left min-h-touch px-4 py-2.5 text-sm hover:bg-brand/10 transition-colors ${
              i18n.language?.startsWith(code) ? "text-brand font-semibold" : "text-gray-700"
            }`}
          >
            {t(labelKey)}
          </button>
        ))}
      </div>,
      document.body
    );

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((isOpen) => !isOpen);
        }}
        className="flex items-center gap-2 min-h-touch px-4 py-2 rounded-xl border-2 border-gray-200 bg-white hover:border-brand/50 hover:bg-brand/5 transition-all text-sm font-medium text-gray-700"
        aria-label={t("language.chooseLanguage")}
        aria-expanded={open}
      >
        <FiGlobe size={18} />
        <span>{t(`language.${i18n.language?.startsWith("en") ? "en" : "fr"}`)}</span>
      </button>
      {menu}
    </div>
  );
};

export default LanguageSelector;
